import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PerformanceMetricsCard from './components/PerformanceMetricsCard';
import WorkloadDistributionChart from './components/WorkloadDistributionChart';
import PerformanceLeaderboard from './components/PerformanceLeaderboard';
import TeamScheduleGantt from './components/TeamScheduleGantt';
import RealTimeTaskUpdates from './components/RealTimeTaskUpdates';
import { taskService } from '../../services/taskService';
import { teamService } from '../../services/teamService';
import { useAuth } from '../../contexts/AuthContext';
import DashboardBottomNav from '../../components/ui/DashboardBottomNav';

const TeamPerformanceHub = () => {
  const { user, loading: authLoading } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState('all-teams');
  const [performancePeriod, setPerformancePeriod] = useState('weekly');
  const [benchmarkToggle, setBenchmarkToggle] = useState(true);
  const [metricsData, setMetricsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading) {
      loadPerformanceData();
    }
  }, [selectedTeam, performancePeriod, authLoading, user]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      setError('');

      const endDate = new Date();
      let startDate = new Date();

      switch (performancePeriod) {
        case 'daily':
          startDate?.setDate(endDate?.getDate() - 1);
          break;
        case 'weekly':
          startDate?.setDate(endDate?.getDate() - 7);
          break;
        case 'monthly':
          startDate?.setMonth(endDate?.getMonth() - 1);
          break;
        case 'quarterly':
          startDate?.setMonth(endDate?.getMonth() - 3);
          break;
        default:
          startDate?.setDate(endDate?.getDate() - 7);
      }

      const filters = {
        dateFrom: startDate?.toISOString(),
        dateTo: endDate?.toISOString()
      };

      if (selectedTeam !== 'all-teams' && user) {
        try {
          const teamMembers = await teamService?.getTeamMembers();
          const filteredMembers = teamMembers?.filter(member => 
            member?.department === selectedTeam || member?.position?.toLowerCase()?.includes(selectedTeam)
          );
          
          if (filteredMembers?.length > 0) {
            filters.assignedTo = filteredMembers?.[0]?.userId;
          }
        } catch (teamError) {
          console.warn('Error fetching team members:', teamError);
        }
      }

      const metrics = await taskService?.getPerformanceMetrics(filters);

      if (!user) {
        setError('Please sign in to view your actual team performance data. Currently showing example data.');
      }

      setMetricsData([
        {
          title: "Task Completion Rate",
          value: metrics?.completionRate || 87,
          unit: "%",
          change: (metrics?.completionRate || 87) >= 85 ? 5.2 : -2.3,
          target: 95,
          icon: "CheckCircle",
          color: (metrics?.completionRate || 87) >= 85 ? "success" : "warning"
        },
        {
          title: "Average Task Time",
          value: Math.round((metrics?.averageDuration || 7200) / 60 * 10) / 10,
          unit: "hours",
          change: -8.1,
          target: 2.0,
          icon: "Clock",
          color: "warning"
        },
        {
          title: "Quality Score",
          value: Math.min(5, Math.max(1, ((metrics?.completionRate || 87) / 100) * 5)),
          unit: "/5.0",
          change: 3.4,
          target: 4.8,
          icon: "Star",
          color: "primary"
        },
        {
          title: "Total Tasks",
          value: metrics?.totalTasks || 42,
          unit: "tasks",
          change: (metrics?.totalTasks || 42) > 0 ? 12.1 : 0,
          target: 50,
          icon: "List",
          color: (metrics?.totalTasks || 42) > 30 ? "success" : "error"
        }
      ]);

    } catch (err) {
      console.error('Error loading performance data:', err);
      setError(err?.message || 'Failed to load performance data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const teamOptions = [
    { value: 'all-teams', label: 'All Teams' },
    { value: 'field-ops', label: 'Field Operations' },
    { value: 'maintenance', label: 'Equipment Maintenance' },
    { value: 'quality-control', label: 'Quality Control' },
    { value: 'data-analysis', label: 'Data Analysis' },
    { value: 'ipm-operations', label: 'IPM Operations' },
    { value: 'management', label: 'Management' }
  ];

  const periodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-muted-foreground">Loading...</span>
            </div>
          </div>
        </main>
        <DashboardBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 pb-20 md:pb-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Team Performance Hub</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Monitor team metrics and performance</p>
        </div>

        {/* Filters - touch-optimized */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Team</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e?.target?.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] touch-manipulation"
              >
                {teamOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
              <select
                value={performancePeriod}
                onChange={(e) => setPerformancePeriod(e?.target?.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] touch-manipulation"
              >
                {periodOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center space-x-3 cursor-pointer min-h-[44px] touch-manipulation">
                <input
                  type="checkbox"
                  checked={benchmarkToggle}
                  onChange={(e) => setBenchmarkToggle(e?.target?.checked)}
                  className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Show Benchmarks</span>
              </label>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {loading ? (
            Array(4)?.fill(0)?.map((_, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 animate-pulse">
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : (
            metricsData?.map((metric, index) => (
              <PerformanceMetricsCard key={index} {...metric} />
            ))
          )}
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <WorkloadDistributionChart />
          <PerformanceLeaderboard />
        </div>

        {/* Team Schedule Gantt */}
        <div className="mb-6 sm:mb-8">
          <TeamScheduleGantt />
        </div>

        {/* Real-Time Task Updates */}
        <div>
          <RealTimeTaskUpdates />
        </div>
      </div>
      <DashboardBottomNav />
    </div>
  );
};

export default TeamPerformanceHub;